import React, { useCallback, useEffect, useRef, useState } from 'react';
import './AutomationWizard.css';
import './ReportsAutomationWizard.css';
import './AlertsAutomationWizard.css';
import AlertStepType from './steps/AlertStepType';
import AlertStepAuthoring from './steps/AlertStepAuthoring';
import AlertStepRecipients from './steps/AlertStepRecipients';
import { isValidReportEmail } from './steps/ReportStepOutput';
import AlertStepSchedule from './steps/AlertStepSchedule';
import { apiService } from '../../../services/api';
import { isMeasurementChange, measurementSignatureOf, validateAlertSpecInputs } from './alertSpec';
import {
  buildCreatePayload,
  buildUpdatePayload,
  clearedGuardFields,
  editBlockers,
  formDataFromAutomation,
  validateGuardInputs,
} from './alertPayload';
import AlertSpecProblems from './steps/AlertSpecProblems';
import { hasAnyRecipient, pendingRecipientProblem, withPendingRecipient } from './alertRecipients';
import { useAlertDryRun } from './useAlertDryRun';
import { useAlertConversation } from './useAlertConversation';
import { defaultScheduleZone } from '../shared/ScheduleZoneField';

const ALERT_STEPS = [
  { number: 1, label: 'Type' },
  { number: 2, label: 'Query' },
  { number: 3, label: 'Recipients' },
  { number: 4, label: 'Schedule' },
];

const initialFormData = () => ({
  type: 'alerts',
  automationName: '',
  reportQuery: '',
  queryName: '',
  reportDatasetIds: [],
  reportEmailToSelf: true,
  reportRecipients: [],
  trigger: { scheduleType: 'scheduled', frequency: 'daily', time: '08:00', dayOfWeek: 'monday' },
  timezone: defaultScheduleZone(),
  alert: {
    // The query the author's question was turned into, and what running it
    // returned. None of it is typed: `sql` and `window` come back from the
    // authoring endpoint and `resultColumns` is what the comparison controls
    // are populated from.
    //
    // `window` is the other half of `sql` — the query holds its period as
    // placeholders, and this is what they bind to on each run. They are set and
    // cleared together for that reason.
    sql: null,
    window: null,
    resultColumns: [],
    // The classed refusal from the authoring endpoint, when the question could
    // not be turned into a query. Held in form state rather than in the hook so
    // it survives a step change, as the query does.
    authorRefusal: null,
    shape: 'threshold',
    subjectColumn: '',
    measureColumn: '',
    comparator: '>=',
    // Exactly one of the two is sent; this says which control is shown.
    thresholdKind: 'value',
    threshold: '',
    thresholdColumn: '',
    periodColumn: '',
    periodUnit: '',
    runLength: '',
    channels: ['email'],
    severity: 'warning',
    // Left empty deliberately. A null cap means the author never set one and
    // the evaluation default applies; pre-filling it would persist an
    // inherited default as an explicit choice.
    volumeGuardMaxMatches: '',
    volumeGuardMaxShare: '',
  },
});

/**
 * The Alerts wizard — a per-type wizard, cloned from the Reports pattern
 * rather than bolted onto the generic six-step builder.
 *
 * The create payload uses **camelCase** alert fields, because that is what
 * `AutomationCreate` takes. `AutomationUpdate` uses snake_case for the same
 * fields; each model follows its own existing convention and that difference is
 * not a mistake for the client to normalise away.
 *
 * **The dry run is owned here, not by each step.** One evaluation serves the
 * live count on step 2, the message previews on step 3 and the volume-guard
 * verdict on step 4. Mounting it per step re-evaluated on every step change and
 * left step 4 with no result at all — which meant the guard warnings could
 * never fire, despite step 4 being the only place the guard is configurable.
 */
const AlertsAutomationWizard = ({
  isOpen,
  onClose,
  onShowNotification,
  initialStep = 1,
  onChooseDifferentTemplate,
  editingAutomation = null,
}) => {
  const isEditMode = Boolean(editingAutomation?.id);
  // Computed before any state so the refusal can short-circuit the whole form.
  const blockers = isEditMode ? editBlockers(editingAutomation) : [];
  // `initialStep` lets the generic builder hand off after its own type step
  // without making the author choose the template twice.
  const [currentStep, setCurrentStep] = useState(
    // Editing skips the type step: the template of an existing alert is not a
    // choice, and the generic builder locks its own type step for the same
    // reason.
    () => (isEditMode ? 2 : Math.min(4, Math.max(1, Number(initialStep) || 1)))
  );
  const [formData, setFormData] = useState(
    () => (isEditMode ? formDataFromAutomation(editingAutomation) : initialFormData())
  );
  // The alert exactly as it was loaded (PRD-1382). "Did the measurement move"
  // compares against the STORED definition, not against the last thing the
  // author did — otherwise someone who changes the query twice is only ever
  // asked about the second change. Computed once; it must not move for the life
  // of the edit.
  const originalAlertRef = useRef(undefined);
  if (originalAlertRef.current === undefined) {
    originalAlertRef.current = isEditMode ? formDataFromAutomation(editingAutomation).alert : null;
  }

  // WHAT the author confirmed they looked at, not merely THAT they confirmed
  // something. A boolean would stay true through a second rebuild, letting an
  // author acknowledge one query and save a different one — the precise
  // substitution this gate exists to prevent.
  const [acknowledgedMeasurement, setAcknowledgedMeasurement] = useState(null);
  const measurementSignature = measurementSignatureOf(formData.alert);
  const measurementAcknowledged = acknowledgedMeasurement !== null
    && acknowledgedMeasurement === measurementSignature;

  const [creating, setCreating] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);
  // Leaving the wizard mid-run must not set state on an unmounted component.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // **The summary is not on a list row.** An edit opens from the Automations
  // list, whose rows come from the list route — and that builds them straight
  // from the model, while `alert_summary` is added by the single-fetch builder.
  // So the opener had nothing to render, and rendered nothing: no error, no
  // empty state, the feature simply absent rather than broken, which is harder
  // to notice. Raised in review of #195.
  //
  // Fetched here rather than fixed on the list route, which would parse every
  // stored spec on every list call for a sentence almost no row will show.
  //
  // Best-effort on purpose: if this fails the opener stays hidden, which is
  // exactly today's behaviour. An edit must not be blocked by a sentence.
  useEffect(() => {
    if (!isOpen || !isEditMode || !editingAutomation?.id) return undefined;
    if (Array.isArray(editingAutomation.alert_summary) && editingAutomation.alert_summary.length) {
      return undefined;
    }
    let cancelled = false;
    apiService
      .getAutomation(editingAutomation.id)
      .then((full) => {
        if (cancelled || !mountedRef.current) return;
        const summary = Array.isArray(full?.alert_summary) ? full.alert_summary : [];
        if (summary.length) setFormData((prev) => ({ ...prev, alertSummary: summary }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, isEditMode, editingAutomation]);

  // Not while the form is refused: an alert this wizard will not open still
  // yields a valid spec from the read-back, and evaluating it would put a real
  // query on the structured-data service for a form nobody sees.
  const dryRun = useAlertDryRun(formData, { enabled: isOpen && blockers.length === 0 });
  // **Owned by the wizard, not step 2** (PRD-1447). The wizard renders only the
  // current step, so a conversation held inside step 2 died the moment the author
  // left it — the transcript, the built alert and the create card with it. It
  // makes no request until the author sends a message, so owning it here costs
  // nothing when the form path is in use instead.
  const conversation = useAlertConversation({ datasetIds: formData.reportDatasetIds ?? [] });

  useEffect(() => {
    if (!isOpen || isEditMode) return undefined;
    let cancelled = false;
    apiService.getCurrentUser()
      .then((user) => {
        if (cancelled || !user) return;
        const email = (user.email || (user.name?.includes('@') ? user.name : '') || '').trim();
        if (email) setFormData((prev) => ({ ...prev, userEmail: email }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, isEditMode]);

  const handleClose = useCallback(() => {
    setCurrentStep(1);
    setFormData(initialFormData());
    onClose();
  }, [onClose]);

  /** Surface a refusal as written — it is addressed to whoever submitted it. */
  const notifyError = useCallback((error, fallback) => {
    const detail = error?.responseData?.detail;
    const message = typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? (detail[0]?.msg || error?.message)
        : error?.message || fallback;
    if (onShowNotification) onShowNotification(message, 'error');
  }, [onShowNotification]);

  const specProblems = validateAlertSpecInputs(formData);
  const guardProblems = validateGuardInputs(formData);
  const datasetsChosen = (formData.reportDatasetIds ?? []).length > 0;
  const datasetProblems = datasetsChosen
    ? []
    : ['Select at least one dataset for the comparison to read.'];
  // Everything standing between this form and a create, in the author's words.
  // The name is included because it gates Create and was previously the one
  // blocker with no stated reason — the exact failure AlertSpecProblems exists
  // to fix. It is editable on this step, so the reason is actionable here.
  const nameProblems = formData.automationName?.trim()
    ? []
    : ['Give the alert a name.'];
  // An edit that blanks a previously-set guard cannot be honoured by the API,
  // which reads an absent value as "leave unchanged" — so block with the
  // reason rather than save and report success.
  //
  // These are passed to step 4's summary, unlike the guard *range* problems,
  // which AlertGuardFields already states inline at the field. Nothing renders
  // a clear problem, so omitting it left Save disabled with no reason shown.
  const clearProblems = isEditMode ? clearedGuardFields(formData, editingAutomation) : [];
  const finalProblems = [
    ...specProblems, ...datasetProblems, ...guardProblems, ...nameProblems,
    ...clearProblems,
  ];

  const handleDryRun = useCallback(async () => {
    // Routed through the hook rather than firing its own request, so the panel
    // and the toast describe one evaluation. Two requests meant two sources of
    // truth: a timed-out hook run left the error banner up while this toasted
    // a successful count.
    setDryRunning(true);
    try {
      const result = await dryRun.runNow();
      if (!result || !onShowNotification) return;
      const guard = result.volumeGuard ?? {};
      const guardNote = guard.wouldTrip
        ? ' The volume guard would trip on this run.'
        : guard.configured && !guard.checked
          ? ' No guard rule applied to this run.'
          : '';
      onShowNotification(
        `Dry run: ${result.subjectsMatched} of ${result.subjectsEvaluated} subjects match.`
        + ` Nothing was sent.${guardNote}`,
        guard.wouldTrip ? 'error' : 'success'
      );
    } finally {
      // The hook owns the error path, so a refusal already shows in the panel.
      if (mountedRef.current) setDryRunning(false);
    }
  }, [dryRun, onShowNotification]);

  const handleSave = useCallback(async () => {
    // A typed address the author never clicked Add on is saved, as step 3's
    // Next would have committed it. On the four-step path Next already has, so
    // this is a no-op there; creating from the conversation skips Next, and
    // used to drop the address (review of #212).
    const delivering = withPendingRecipient(formData);
    const payload = isEditMode
      ? buildUpdatePayload(delivering)
      : buildCreatePayload(delivering);
    // Gated on the handler as well as the button: an out-of-range guard must
    // not reach the payload, where it would be silently dropped.
    if (!payload || guardProblems.length > 0) return;
    setCreating(true);
    try {
      if (isEditMode) {
        await apiService.updateAutomation(editingAutomation.id, payload);
      } else {
        await apiService.createAutomation(payload);
      }
      if (onShowNotification) {
        onShowNotification(
          isEditMode
            ? 'Alert updated. Its active state is unchanged.'
            : 'Alert created. It is inactive until you activate it.',
          'success'
        );
      }
      handleClose();
    } catch (error) {
      notifyError(error, isEditMode ? 'Failed to update alert' : 'Failed to create alert');
    } finally {
      setCreating(false);
    }
  }, [
    formData, guardProblems.length, onShowNotification, notifyError, handleClose,
    isEditMode, editingAutomation,
  ]);

  /**
   * Advance a step — but on the recipients step, first validate and commit any
   * address typed into "Email To Others" without clicking Add.
   *
   * `ReportStepOutput` moved that pending value into `formData` (PRD-1269) so
   * a wizard can do exactly this. The Reports wizard does; without the same
   * here, a typed-but-not-added address is silently dropped in the Alerts flow.
   */
  const handleNext = () => {
    if (currentStep === 3) {
      const pending = (formData.pendingRecipient || '').trim();
      if (pending) {
        if (!isValidReportEmail(pending)) {
          setFormData((f) => ({
            ...f,
            recipientError: 'Enter a valid email address (e.g. name@example.com).',
          }));
          return;
        }
        setFormData((f) => {
          const recips = f.reportRecipients ?? [];
          return {
            ...f,
            reportRecipients: recips.includes(pending) ? recips : [...recips, pending],
            pendingRecipient: '',
            recipientError: '',
          };
        });
      }
    }
    setCurrentStep((step) => step + 1);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.type === 'alerts';
      case 2: {
        if (!datasetsChosen || specProblems.length > 0) return false;
        // PRD-1382. A change to what the alert measures cannot be saved until
        // the author has looked at what it now matches. On create the preview is
        // self-policing — a bad generation is visible and gets rejected — but an
        // edit already worked, so nothing forces the second look.
        if (isEditMode && isMeasurementChange(originalAlertRef.current, formData.alert)) {
          return measurementAcknowledged;
        }
        return true;
      }
      case 3: {
        // AC8 — no recipient blocks the step; a channel is required too,
        // because the API refuses an empty channel list.
        const pending = (formData.pendingRecipient || '').trim();
        // A typed address counts as a recipient for the purpose of advancing —
        // handleNext commits it — but only if it is actually valid.
        if (pending && !isValidReportEmail(pending)) return false;
        const hasSomeone = hasAnyRecipient(formData) || !!pending;
        return hasSomeone && (formData.alert?.channels ?? []).length > 0;
      }
      case 4:
        return finalProblems.length === 0;
      default:
        return false;
    }
  };

  // **Everything the last step would check, plus what the Recipients step does**
  // (PRD-1447). Creating from the conversation skips steps 3 and 4, so it must
  // not skip their checks. `finalProblems` is step 4's list; step 3 enforces
  // "someone to send to" and "at least one channel" in `canProceed` instead, and
  // the API refuses an empty channel list on create. Both pass on the defaults,
  // which is why the conversation can usually create — but an author who cleared
  // them on step 3 and came back must not be able to save around the check.
  //
  // A typed address is read the way `canProceed` reads it (review of #212): an
  // invalid one blocks, as it blocks Next, and a valid one counts as someone,
  // because `handleSave` folds it in.
  const typedAddressProblem = pendingRecipientProblem(formData);
  const deliveryProblems = [
    ...(typedAddressProblem ? [typedAddressProblem] : []),
    ...(hasAnyRecipient(withPendingRecipient(formData)) ? [] : ['Choose who the alert is sent to.']),
    ...((formData.alert?.channels ?? []).length > 0 ? [] : ['Choose how the alert is sent.']),
  ];
  const createFromConversationProblems = [...finalProblems, ...deliveryProblems];

  // Why the live count is not running, when it is not.
  const notReadyReason = guardProblems.length > 0
    ? 'The volume guard is out of range, so no evaluation is run — fix it to see the count.'
    : datasetProblems.length > 0
      ? 'Select at least one dataset to see how many subjects match.'
      : specProblems.length > 0
        ? 'Complete the comparison to see how many subjects match.'
        : null;

  const renderStep = () => {
    const props = { formData, onSetFormData: setFormData };
    if (currentStep === 1) {
      return <AlertStepType {...props} onChooseDifferentTemplate={onChooseDifferentTemplate} />;
    }
    if (currentStep === 2) {
      return (
        <AlertStepAuthoring
          {...props}
          dryRun={dryRun}
          notReadyReason={notReadyReason}
          isEditMode={isEditMode}
          originalAlert={originalAlertRef.current}
          measurementAcknowledged={measurementAcknowledged}
          onAcknowledgeMeasurement={() => setAcknowledgedMeasurement(measurementSignature)}
          problems={[...specProblems, ...datasetProblems]}
          // Create straight from the conversation (PRD-1447). The same save the
          // last step runs, gated on the same checks plus the Recipients step's.
          onCreateNow={handleSave}
          creating={creating}
          createProblems={createFromConversationProblems}
          onEditDelivery={() => setCurrentStep(3)}
          // Owned here so it outlives a step change — see AlertStepConversation.
          conversation={conversation}
        />
      );
    }
    if (currentStep === 3) return <AlertStepRecipients {...props} dryRun={dryRun} />;
    // Guard problems are deliberately excluded here: AlertGuardFields states
    // them inline at the input they belong to, which is on this same step, so
    // repeating them in the summary would print each one twice.
    return (
      <AlertStepSchedule
        {...props}
        dryRun={dryRun}
        notReadyReason={notReadyReason}
        problems={[...specProblems, ...datasetProblems, ...clearProblems]}
      />
    );
  };

  if (!isOpen) return null;

  // Refuse rather than repair. Loading a spec this form cannot represent and
  // then saving it would drop whatever there is no field for, and the alert
  // would break on its next run. An alert nobody can edit here is recoverable;
  // one that edits itself into nonsense is not.
  if (blockers.length > 0) {
    return (
      <div className="reports-automation-wizard-page">
        <div className="reports-wizard-container">
          <div className="reports-wizard-header">
            <div className="reports-wizard-header-row">
              <h2 className="reports-wizard-title">
                Cannot edit “{editingAutomation.name || 'this alert'}” here
              </h2>
              <button type="button" className="reports-wizard-cancel" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
          <div className="reports-wizard-content">
            <AlertSpecProblems problems={blockers} heading="Why" />
            <p className="alert-guard-intro">
              Nothing has been changed. The alert keeps running as configured.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-automation-wizard-page">
      <div className="reports-wizard-container">
        <div className="reports-wizard-header">
          <div className="reports-wizard-breadcrumb">
            <span onClick={handleClose}>Automations</span>
            <span className="reports-wizard-breadcrumb-sep">/</span>
            <span className="reports-wizard-breadcrumb-current">
              {isEditMode ? `Edit: ${editingAutomation.name || 'Alert'}` : 'New alert'}
            </span>
          </div>
          <div className="reports-wizard-header-row">
            <h2 className="reports-wizard-title">
              {isEditMode ? 'Edit alert' : 'New alert'}
            </h2>
            <button type="button" className="reports-wizard-cancel" onClick={handleClose}>
              Cancel
            </button>
          </div>
        </div>

        <div className="reports-wizard-steps">
          {ALERT_STEPS.map((step) => (
            <div
              key={step.number}
              className={`reports-wizard-step ${currentStep === step.number ? 'reports-wizard-step-active' : ''} ${currentStep > step.number ? 'reports-wizard-step-done' : ''}`}
            >
              <span className="reports-wizard-step-num">{step.number}.</span>
              <span>{step.label}</span>
            </div>
          ))}
        </div>

        <div className="reports-wizard-content">{renderStep()}</div>

        <div className="reports-wizard-footer">
          {currentStep > (isEditMode ? 2 : 1) && (
            <button
              type="button"
              className="reports-wizard-btn reports-wizard-btn-back"
              onClick={() => setCurrentStep((s) => s - 1)}
            >
              Back
            </button>
          )}
          {currentStep === 4 ? (
            <div className="alert-wizard-final-actions">
              <button
                type="button"
                className="resui-btn resui-btn--secondary"
                onClick={handleDryRun}
                disabled={dryRunning || finalProblems.length > 0}
              >
                {dryRunning ? 'Running…' : 'Dry run'}
              </button>
              <button
                type="button"
                className="reports-wizard-btn reports-wizard-btn-primary"
                onClick={handleSave}
                disabled={creating || !canProceed()}
              >
                {creating ? 'Saving…' : isEditMode ? 'Save changes' : 'Create automation'}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="reports-wizard-btn reports-wizard-btn-primary"
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertsAutomationWizard;
